"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import PremiumLoader from "@/components/premium-loader";
import WhatsAppFloat from "@/components/whatsapp-float";

type AppShellProps = {
  children: ReactNode;
};

export default function AppShell({ children }: AppShellProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [displayProgress, setDisplayProgress] = useState(0);
  const targetProgressRef = useRef(0);
  const revealTimeoutRef = useRef<number | null>(null);
  const revealScheduledRef = useRef(false);

  useEffect(() => {
    let rafId = 0;
    let isDisposed = false;
    const startedAt = performance.now();
    const hardTimeoutMs = 3500;
    const softProgressMs = 900;
    const cleanupImageListeners: Array<() => void> = [];
    let hasAllTasksFinished = false;

    const criticalImages = Array.from(document.images).filter((image) => {
      const loading = image.getAttribute("loading");
      const fetchPriority = image.getAttribute("fetchpriority");
      return loading === "eager" || fetchPriority === "high";
    });

    const totalTasks = criticalImages.length + 2;
    let completedTasks = 0;

    const markProgress = () => {
      completedTasks += 1;
      const rawProgress = (Math.min(completedTasks, totalTasks) / totalTasks) * 100;
      targetProgressRef.current = Math.max(targetProgressRef.current, rawProgress);
      if (completedTasks >= totalTasks) {
        hasAllTasksFinished = true;
        targetProgressRef.current = 100;
      }
    };

    const onReady = () => {
      if (revealScheduledRef.current) return;
      revealScheduledRef.current = true;
      const elapsed = performance.now() - startedAt;
      const remaining = Math.max(0, 300 - elapsed);
      revealTimeoutRef.current = window.setTimeout(() => {
        if (!isDisposed) {
          setIsLoaded(true);
        }
      }, remaining);
    };

    const animateProgress = () => {
      setDisplayProgress((current) => {
        const elapsed = performance.now() - startedAt;
        const timeBasedTarget = Math.min((elapsed / softProgressMs) * 92, 92);
        if (!hasAllTasksFinished) {
          targetProgressRef.current = Math.max(targetProgressRef.current, timeBasedTarget);
        }

        if (elapsed >= hardTimeoutMs && targetProgressRef.current < 100) {
          hasAllTasksFinished = true;
          targetProgressRef.current = 100;
        }

        const target = targetProgressRef.current;
        if (current >= 100) {
          return 100;
        }

        const next = current + (target - current) * 0.2;
        const bounded = next > 99.9 && target >= 100 ? 100 : next;

        if (bounded >= 100 && target >= 100) {
          onReady();
        }

        return bounded;
      });
      rafId = window.requestAnimationFrame(animateProgress);
    };

    if (document.fonts?.ready) {
      document.fonts.ready.finally(markProgress);
    } else {
      markProgress();
    }

    const onWindowLoad = () => markProgress();

    if (document.readyState === "complete") {
      markProgress();
    } else {
      window.addEventListener("load", onWindowLoad, { once: true });
    }

    criticalImages.forEach((image) => {
      if (image.complete) {
        markProgress();
        return;
      }

      const finish = () => markProgress();
      image.addEventListener("load", finish, { once: true });
      image.addEventListener("error", finish, { once: true });
      cleanupImageListeners.push(() => {
        image.removeEventListener("load", finish);
        image.removeEventListener("error", finish);
      });
    });

    rafId = window.requestAnimationFrame(animateProgress);

    return () => {
      isDisposed = true;
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("load", onWindowLoad);
      cleanupImageListeners.forEach((cleanup) => cleanup());
      if (revealTimeoutRef.current) {
        window.clearTimeout(revealTimeoutRef.current);
      }
      revealScheduledRef.current = false;
    };
  }, []);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    if (!isLoaded) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }

    document.body.style.overflow = originalOverflow;
    return undefined;
  }, [isLoaded]);

  return (
    <>
      {!isLoaded && <PremiumLoader progress={displayProgress} />}
      <div
        className={`transition-opacity duration-500 ${
          isLoaded ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        {children}
        {isLoaded && <WhatsAppFloat />}
      </div>
    </>
  );
}
